import React, { useEffect, useState } from 'react';
import { ArrowRight, Calendar, Users, Map, X, Sparkles, Send, Mic, Heart, Camera, Music, Palette, MapPin, Cake, Gift, Circle, Car, Home, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatStore } from './chat/chatStore.ts';
import { useChatbot } from './chat/GlobalChatbot.tsx';

const WeddingImageCarousel: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const weddingImages = [
    { src: '/landingPage/Classic.jpg', alt: 'Classic Wedding Setup' },
    { src: '/landingPage/Modern.jpg', alt: 'Modern Wedding Design' },
    { src: '/landingPage/BeachWhite.jpeg', alt: 'Beach Wedding Ceremony' },
    { src: '/landingPage/Bohem.jpg', alt: 'Bohemian Wedding Style' },
    { src: '/themesOption/Luxury.jpg', alt: 'Luxury Wedding Venue' },
    { src: '/themesOption/Outdoor.jpg', alt: 'Outdoor Wedding Setting' },
    { src: '/themesOption/Vineyard.jpg', alt: 'Vineyard Wedding' },
    { src: '/Connect/Greek.jpg', alt: 'Greek Wedding Celebration' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % weddingImages.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [weddingImages.length]);

  return (
    <div className="relative w-full h-auto aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
      <AnimatePresence mode="wait">
        <motion.img
          key={currentImageIndex}
          src={weddingImages[currentImageIndex].src}
          alt={weddingImages[currentImageIndex].alt}
          className="w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        />
      </AnimatePresence>
      
      {/* Image indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {weddingImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex 
                ? 'bg-white shadow-lg scale-125' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Image title overlay */}
      <div className="absolute top-4 left-4">
        <motion.div
          key={currentImageIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ delay: 0.3 }}
          className="bg-black/20 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-medium"
        >
          {weddingImages[currentImageIndex].alt}
        </motion.div>
      </div>
    </div>
  );
};

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [isChatActivated, setIsChatActivated] = useState(false);
  
  const { messages, sendMessage, isLoading } = useChatStore();
  const { openChat, isOpen: isChatOpen } = useChatbot();

  const videos = ['/heroVideos/hero2.mp4', '/heroVideos/hero3.mp4', '/heroVideos/hero5.mp4'];

  // Three rows of prompt cards
  const topRowCards = [
    { icon: Calendar, title: "Wedding Timeline", prompt: "Help me create a 12-month wedding timeline", color: "from-blue-500 to-blue-600", bgColor: "bg-blue-50" },
    { icon: Palette, title: "Theme Design", prompt: "Design a bohemian wedding theme", color: "from-purple-500 to-purple-600", bgColor: "bg-purple-50" },
    { icon: Camera, title: "Photography", prompt: "Find wedding photographers in my area", color: "from-pink-500 to-pink-600", bgColor: "bg-pink-50" },
    { icon: MapPin, title: "Venue Search", prompt: "Show me outdoor wedding venues", color: "from-emerald-500 to-emerald-600", bgColor: "bg-emerald-50" },
    { icon: Heart, title: "Floral Design", prompt: "Create a romantic bridal bouquet", color: "from-rose-500 to-rose-600", bgColor: "bg-rose-50" },
    { icon: Music, title: "Wedding Music", prompt: "Create the perfect wedding playlist", color: "from-indigo-500 to-indigo-600", bgColor: "bg-indigo-50" },
    { icon: Cake, title: "Wedding Cake", prompt: "Design a three-tier wedding cake", color: "from-orange-500 to-orange-600", bgColor: "bg-orange-50" },
    { icon: Circle, title: "Wedding Rings", prompt: "Help me choose wedding ring styles", color: "from-yellow-500 to-yellow-600", bgColor: "bg-yellow-50" }
  ];

  const middleRowCards = [
    { icon: Users, title: "Guest List", prompt: "Help organize my guest list and seating", color: "from-amber-500 to-amber-600", bgColor: "bg-amber-50" },
    { icon: Sparkles, title: "Budget Planning", prompt: "Create a realistic wedding budget", color: "from-teal-500 to-teal-600", bgColor: "bg-teal-50" },
    { icon: Gift, title: "Wedding Favors", prompt: "Suggest unique wedding favor ideas", color: "from-cyan-500 to-cyan-600", bgColor: "bg-cyan-50" },
    { icon: Car, title: "Transportation", prompt: "Plan wedding day transportation", color: "from-slate-500 to-slate-600", bgColor: "bg-slate-50" },
    { icon: Home, title: "Reception Venue", prompt: "Find reception venues with catering", color: "from-green-500 to-green-600", bgColor: "bg-green-50" },
    { icon: Star, title: "Wedding Decor", prompt: "Design ceremony arch decorations", color: "from-violet-500 to-violet-600", bgColor: "bg-violet-50" },
    { icon: Calendar, title: "Honeymoon", prompt: "Plan the perfect honeymoon destination", color: "from-fuchsia-500 to-fuchsia-600", bgColor: "bg-fuchsia-50" },
    { icon: Heart, title: "Wedding Attire", prompt: "Help me choose wedding dress styles", color: "from-red-500 to-red-600", bgColor: "bg-red-50" }
  ];

  // Duplicate arrays for infinite scroll effect
  const topRowDouble = [...topRowCards, ...topRowCards];
  const middleRowDouble = [...middleRowCards, ...middleRowCards];

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleVideoEnd = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/design');
    } else {
      navigate('/login');
    }
  };

  const handlePromptClick = (prompt: string) => {
    setSearchInput(prompt);
    handleChatSend(prompt);
  };

  const handleChatSend = async (message: string) => {
    if (!message.trim()) return;
    
    setIsChatActivated(true);
    setTimeout(() => {
      openChat();
      setTimeout(async () => {
        try {
          await sendMessage(message);
        } catch (error) {
          console.error('Error sending message:', error);
        }
      }, 300);
    }, 800);
  };

  return (
    <section className="pt-48 pb-20 relative overflow-hidden bg-white">
      <div className="container-custom relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2 space-y-6">
            <h1 className="text-[56px] font-bold font-cormorant-garamond leading-[1.1] text-[#1C1C1E]">
              Plan Your <span className="italic text-[#FF4E8E]">Perfect</span> <span className="text-[#FF4E8E]">Wedding</span> With Ease
            </h1>
            <p className="text-[18px] font-cormorant-garamond font-normal text-[#535353] leading-[1.6] tracking-[0.2px] max-w-xl">
              Streamline your wedding planning journey with our AI-powered platform. From venue selection to guest management, we've got you covered.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button 
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] hover:opacity-90 text-white px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-[18px] font-cormorant-garamond font-bold"
              >
                Get Started <ArrowRight size={18} />
              </button>
              <button 
                onClick={() => setShowVideoModal(true)}
                className="hover:bg-[#5D5FEF]/20 px-6 py-3 rounded-lg transition-all duration-300 text-[18px] font-cormorant-garamond font-bold"
              >
                See How It Works
              </button>
            </div>
            
            <div className="pt-8 flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <div className="w-10 h-10 rounded-full bg-[#FF4E8E]/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-[#FF4E8E]" />
                </div>
                <span className="font-cormorant-garamond font-medium text-[16px] text-[#1C1C1E]">Smart Planning</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#5D5FEF]/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#5D5FEF]" />
                </div>
                <span className="font-cormorant-garamond font-medium text-[16px] text-[#1C1C1E]">Vendor Network</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#2DCEA8]/10 flex items-center justify-center">
                  <Palette className="h-5 w-5 text-[#2DCEA8]" />
                </div>
                <span className="font-cormorant-garamond font-medium text-[16px] text-[#1C1C1E]">Design</span>
              </div>
            </div>
          </div>
          
          <div className="lg:w-1/2">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#FF69B4]/15 to-[#FFDAB9]/15 p-8 sm:p-10 md:p-12 rounded-3xl">
                <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-xl relative z-10 border border-[#FF4E8E]/20">
                  <WeddingImageCarousel />
                  
                  <div className="mt-8 grid grid-cols-2 gap-6">
                    <div className="bg-[#FF4E8E]/5 p-4 text-center rounded-xl border border-[#FF4E8E]/20">
                      <h3 className="font-semibold mb-1 text-[#1C1C1E]">120+</h3>
                      <p className="text-sm text-[#535353]">Vendor Partners</p>
                    </div>
                    <div className="bg-[#FF4E8E]/5 p-4 text-center rounded-xl border border-[#FF4E8E]/20">
                      <h3 className="font-semibold mb-1 text-[#1C1C1E]">25K+</h3>
                      <p className="text-sm text-[#535353]">Happy Couples</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements with enhanced glow effect */}
              <div className="absolute -top-8 -left-8 w-32 h-32 rounded-2xl bg-[#FF4E8E]/5 border border-[#FF4E8E]/20 glow-pink"></div>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-2xl bg-[#5D5FEF]/5 border border-[#5D5FEF]/20 glow-indigo"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="relative w-full max-w-4xl mx-4">
            <button 
              onClick={() => {
                setShowVideoModal(false);
                setCurrentVideoIndex(0);
              }}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X size={32} />
            </button>
            <video
              key={videos[currentVideoIndex]}
              className="w-full rounded-lg shadow-2xl"
              autoPlay
              onEnded={handleVideoEnd}
              controls
            >
              <source src={videos[currentVideoIndex]} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
                 </div>
       )}

       {/* Floating Prompt Cards */}
       <div className="mt-20 mb-20 relative">
         <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.3 }}
           className="relative z-10 max-w-7xl mx-auto px-4"
         >
           <div className="text-center mb-12">
             <motion.h3 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ delay: 0.5 }}
               className="text-2xl font-bold bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] bg-clip-text text-transparent mb-3"
             >
               Ask Celine Anything
             </motion.h3>
             <motion.p 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ delay: 0.7 }}
               className="text-gray-600 font-medium"
             >
               Click any prompt to start planning your perfect wedding
             </motion.p>
           </div>

           {/* Floating Cards Row 1 */}
           <div className="relative overflow-hidden mb-4">
             <motion.div
               animate={{ x: [-100, 0] }}
               transition={{
                 duration: 25,
                 repeat: Infinity,
                 ease: "linear"
               }}
               className="flex gap-4 w-fit"
             >
               {topRowDouble.map((card, index) => (
                 <motion.button
                   key={`top-${index}`}
                   onClick={() => handlePromptClick(card.prompt)}
                   whileHover={{ 
                     scale: 1.05, 
                     y: -5,
                   }}
                   whileTap={{ scale: 0.98 }}
                   className="flex-shrink-0 w-64 h-16 bg-white/30 backdrop-blur-md border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left group hover:bg-white/40"
                 >
                   <div className="flex items-center h-full px-4 space-x-3">
                     <div className={`w-8 h-8 rounded-xl bg-gradient-to-r ${card.color} flex items-center justify-center shadow-md`}>
                       <card.icon className="w-4 h-4 text-white" />
                     </div>
                     <div className="flex-1 min-w-0">
                       <p className="text-sm font-medium text-gray-800 leading-tight truncate group-hover:text-gray-900">
                         {card.prompt}
                       </p>
                     </div>
                   </div>
                 </motion.button>
               ))}
             </motion.div>
           </div>

           {/* Floating Cards Row 2 */}
           <div className="relative overflow-hidden">
             <motion.div
               animate={{ x: [0, -100] }}
               transition={{
                 duration: 20,
                 repeat: Infinity,
                 ease: "linear"
               }}
               className="flex gap-4 w-fit"
             >
               {middleRowDouble.map((card, index) => (
                 <motion.button
                   key={`middle-${index}`}
                   onClick={() => handlePromptClick(card.prompt)}
                   whileHover={{ 
                     scale: 1.05, 
                     y: -5,
                   }}
                   whileTap={{ scale: 0.98 }}
                   className="flex-shrink-0 w-64 h-16 bg-white/30 backdrop-blur-md border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left group hover:bg-white/40"
                 >
                   <div className="flex items-center h-full px-4 space-x-3">
                     <div className={`w-8 h-8 rounded-xl bg-gradient-to-r ${card.color} flex items-center justify-center shadow-md`}>
                       <card.icon className="w-4 h-4 text-white" />
                     </div>
                     <div className="flex-1 min-w-0">
                       <p className="text-sm font-medium text-gray-800 leading-tight truncate group-hover:text-gray-900">
                         {card.prompt}
                       </p>
                     </div>
                   </div>
                 </motion.button>
               ))}
             </motion.div>
           </div>
         </motion.div>

         {/* Background Gradient */}
         <div className="absolute inset-0 bg-gradient-to-r from-[#FF69B4]/5 to-[#FF7F50]/5 rounded-3xl -z-10"></div>
       </div>

       {/* Chat Interface Section */}
       <div className="mt-20 max-w-4xl mx-auto">
         <AnimatePresence mode="wait">
           {!isChatActivated && (
             <motion.div
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               exit={{ 
                 opacity: 0, 
                 y: -40,
                 scale: 0.95,
                 filter: "blur(8px)"
               }}
               transition={{ 
                 duration: 0.6,
                 ease: [0.25, 0.46, 0.45, 0.94]
               }}
               className="text-center space-y-6"
             >
             
               {/* Chat Input */}
               <motion.div
                 initial={{ opacity: 0, y: 30 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.5 }}
                 className="relative w-full max-w-3xl mx-auto"
               >
                 <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
                   <div className="px-6 py-4">
                     <div className="relative">
                       <div className="flex items-center bg-gray-50/80 rounded-xl border-2 border-gray-200/60 focus-within:border-purple-400 focus-within:bg-white transition-all duration-300 shadow-sm hover:shadow-md">
                         <div className="flex-1">
                           <input
                             type="text"
                             value={searchInput}
                             onChange={(e) => setSearchInput(e.target.value)}
                             placeholder="Ask Celine anything about your wedding..."
                             className="w-full px-4 py-3 bg-transparent text-base placeholder-gray-500 focus:outline-none transition-all duration-200"
                             onKeyPress={(e) => {
                               if (e.key === 'Enter' && searchInput.trim()) {
                                 handleChatSend(searchInput);
                                 setSearchInput('');
                               }
                             }}
                           />
                         </div>
                         
                         <div className="flex items-center space-x-1 pr-2">
                           <button className="p-2 text-gray-400 hover:text-purple-600 transition-colors rounded-lg hover:bg-gray-100/50">
                             <Mic className="w-4 h-4" />
                           </button>
                           <button 
                             onClick={() => {
                               if (searchInput.trim()) {
                                 handleChatSend(searchInput);
                                 setSearchInput('');
                               }
                             }}
                             className="p-2 bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] text-white rounded-lg hover:opacity-90 transition-all shadow-md hover:shadow-lg hover:scale-105"
                           >
                             <Send className="w-4 h-4" />
                           </button>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </motion.div>
             </motion.div>
           )}

           {/* Chat Activation Transition */}
           {isChatActivated && (
             <motion.div
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               exit={{ opacity: 0, scale: 0.9 }}
               transition={{ duration: 0.4 }}
               className="max-w-2xl mx-auto text-center space-y-8"
             >
               <motion.div
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.2 }}
                 className="space-y-6"
               >
                 <motion.div
                   animate={{
                     scale: [1, 1.05, 1],
                   }}
                   transition={{
                     duration: 2,
                     repeat: Infinity,
                     ease: "easeInOut"
                   }}
                   className="flex items-center justify-center"
                 >
                   <div className="w-20 h-20 bg-gradient-to-br from-[#FF69B4] to-[#FF7F50] rounded-full flex items-center justify-center shadow-lg">
                     <span className="text-3xl">👩‍💼</span>
                   </div>
                 </motion.div>

                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   transition={{ delay: 0.4 }}
                   className="space-y-3"
                 >
                   <h3 className="text-2xl font-bold text-gray-900">
                     Starting your conversation with Celine...
                   </h3>
                   <p className="text-gray-600">
                     Preparing your personalized wedding planning experience
                   </p>
                 </motion.div>

                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   transition={{ delay: 0.6 }}
                   className="flex justify-center space-x-2"
                 >
                   {[0, 1, 2].map((i) => (
                     <motion.div
                       key={i}
                       animate={{
                         y: [0, -8, 0],
                         opacity: [0.5, 1, 0.5]
                       }}
                       transition={{
                         duration: 1.2,
                         repeat: Infinity,
                         delay: i * 0.2,
                         ease: "easeInOut"
                       }}
                       className="w-2 h-2 bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] rounded-full"
                     />
                   ))}
                 </motion.div>
               </motion.div>
             </motion.div>
           )}
         </AnimatePresence>
       </div>

       <style>{`
        .glow-pink {
          box-shadow: 0 0 60px 20px rgba(255, 78, 142, 0.15),
                      0 0 100px 30px rgba(255, 78, 142, 0.1);
          animation: glow-pulse-pink 4s ease-in-out infinite;
        }
        .glow-indigo {
          box-shadow: 0 0 60px 20px rgba(93, 95, 239, 0.15),
                      0 0 100px 30px rgba(93, 95, 239, 0.1);
          animation: glow-pulse-indigo 4s ease-in-out infinite;
        }
        @keyframes glow-pulse-pink {
          0%, 100% { 
            box-shadow: 0 0 60px 20px rgba(255, 78, 142, 0.15),
                        0 0 100px 30px rgba(255, 78, 142, 0.1);
          }
          50% { 
            box-shadow: 0 0 80px 30px rgba(255, 78, 142, 0.2),
                        0 0 120px 40px rgba(255, 78, 142, 0.15);
          }
        }
        @keyframes glow-pulse-indigo {
          0%, 100% { 
            box-shadow: 0 0 60px 20px rgba(93, 95, 239, 0.15),
                        0 0 100px 30px rgba(93, 95, 239, 0.1);
          }
          50% { 
            box-shadow: 0 0 80px 30px rgba(93, 95, 239, 0.2),
                        0 0 120px 40px rgba(93, 95, 239, 0.15);
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;